import { defineStore } from 'pinia';
import { io } from 'socket.io-client';
import { markRaw, ref, toRaw } from 'vue';
import { getRuntimeConfig, isCapacitor } from '../config/runtime';
import { onDesktopLauncherHome } from '../services/launcherSurface';
import { getOrCreateDeviceFingerprint } from '../utils/deviceFingerprint';
import { useEntitiesStore } from './entities';
import { useAuthStore } from './auth';
import { webRTCStatusBridge } from '../services/webrtcStatusBridge';
import { useWebRTCStore } from './webrtc';
import { refreshDemoFrame, resetClockSync } from '../services/clockSync';
import { attachDemoClockListeners } from '../composables/useDemoClock';
import { attachDemoFeedListeners, resetDemoFeed } from '../composables/useDemoFeed';
import { attachDemoNarrationListeners } from '../composables/useDemoNarration';

const DEBUG_SOCKET = import.meta.env.VITE_DEBUG_SOCKET === 'true';

function debugSocket(...args: unknown[]): void {
  if (DEBUG_SOCKET) {
    console.log(...args);
  }
}

/**
 * True when the given ref value is the WebRTC status bridge.
 *
 * `socket` is a plain Vue ref(), and Vue returns a reactive Proxy from
 * `ref.value` for object values — `socket.value === webRTCStatusBridge` is
 * therefore ALWAYS false. Unwrap with toRaw() before comparing (the bridge is
 * also markRaw()ed where it is assigned, so future readers get the raw object).
 */
function isStatusBridge(value: unknown): boolean {
  return value != null && (value === statusBridgeSocket || toRaw(value) === statusBridgeSocket);
}

/** Compare a socket.io manager URI with a target URL (both may be relative). */
function sameSocketTarget(existingUrl: unknown, targetUrl: string): boolean {
  if (typeof existingUrl !== 'string' || !existingUrl) return false;
  const normalize = (url: string): string => {
    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      return url;
    }
  };
  return normalize(existingUrl) === normalize(targetUrl);
}

/**
 * A listener registered on the store's handle. Deliberately permissive: a
 * handler names the arguments it expects (`(data: { deviceId: string }) => …`)
 * and the payload is whatever the hub sent on that event, so each handler is
 * the one that knows its shape — `never` is what lets all of them be accepted.
 */
export type SocketHandler = (...args: never[]) => void;

/**
 * The store's one socket handle, whichever transport carries it.
 *
 * Local / cloud-HTTP mode: a real socket.io client (`io(…)` answers every
 * member below). Cloud mode over WebRTC: `statusBridgeSocket`, the WebRTC
 * status bridge behind the same members — `connect()` swaps the handle under
 * the listeners so every `socketStore.socket?.on(…)` keeps working across the
 * switch.
 *
 * `id` and `disconnect` are socket.io's own and stay optional: the status
 * channel has no equivalent, and a bridged session has no socket id to report.
 * Call sites check before using them.
 */
export interface HubSocket {
  connected: boolean;
  on(event: string, callback: SocketHandler): unknown;
  off(event: string, callback?: SocketHandler): unknown;
  emit(event: string, ...args: unknown[]): unknown;
  once(event: string, callback: SocketHandler): unknown;
  id?: string;
  disconnect?(): void;
}

/**
 * The WebRTC status bridge, seen as the store's handle.
 *
 * The bridge answers `on`/`off`/`emit`/`connected` itself, but its listener set
 * is typed `(...args: unknown[]) => void`, which the permissive `SocketHandler`
 * above is not assignable to. `Reflect.apply` hands the handler over unchanged,
 * so `off(event, handler)` still removes the exact function `on` registered.
 *
 * `once` is socket.io's and the bridge has none — the one consumer that calls
 * it (`components/panel/PanelIntercomPage.vue`, waiting for the callee's
 * answer) would otherwise throw a TypeError while the panel is in cloud mode,
 * so a one-shot listener is implemented here.
 *
 * `id` and `disconnect` are deliberately absent, exactly as on the bridge: the
 * store's `disconnect()` checks with `typeof` before calling.
 */
function bridgeSocketHandle(): HubSocket {
  const handle: HubSocket = {
    get connected() {
      return webRTCStatusBridge.connected;
    },
    on(event, callback) {
      Reflect.apply(webRTCStatusBridge.on, webRTCStatusBridge, [event, callback]);
      return handle;
    },
    off(event, callback) {
      Reflect.apply(webRTCStatusBridge.off, webRTCStatusBridge, callback ? [event, callback] : [event]);
      return handle;
    },
    emit(event, ...args) {
      return webRTCStatusBridge.emit(event, ...args);
    },
    once(event, callback) {
      const oneShot: SocketHandler = (...args) => {
        handle.off(event, oneShot);
        Reflect.apply(callback, undefined, args);
      };
      Reflect.apply(webRTCStatusBridge.on, webRTCStatusBridge, [event, oneShot]);
      return handle;
    },
  };
  return handle;
}

/** One handle for the bridge singleton — the store swaps to it, never rebuilds it. */
const statusBridgeSocket = bridgeSocketHandle();

export const useSocketStore = defineStore('socket', () => {
  const socket = ref<HubSocket | null>(null);
  const connected = ref(false);
  const lastDisconnectTime = ref<number>(0);
  // The URL the live socket was opened for. socket.io's manager URI is not part
  // of its public types, and this is the same string the target check normalizes.
  let socketTargetUrl: string | null = null;
  const webrtcStore = useWebRTCStore();
  /**
   * The demo clock's listeners on the current socket handle (card D1). Kept so
   * a deliberate disconnect removes them with the socket they were registered
   * on, instead of leaving them on a dead handle.
   */
  let detachDemoClock: (() => void) | null = null;
  let detachDemoFeed: (() => void) | null = null;
  let detachDemoNarration: (() => void) | null = null;

  // The status bridge is a plain (non-reactive) singleton, so a Vue watcher on
  // `webRTCStatusBridge.connected` would never fire. Mirror its lifecycle via
  // its own events instead, while the store is actually bridged to it.
  webRTCStatusBridge.on('connect', () => {
    if (isStatusBridge(socket.value)) {
      connected.value = true;
      lastDisconnectTime.value = 0;
    }
  });
  webRTCStatusBridge.on('disconnect', () => {
    if (isStatusBridge(socket.value)) {
      connected.value = false;
      lastDisconnectTime.value = Date.now();
    }
  });

  function resolveSocketUrl(rawUrl: string): string {
    if (typeof window === 'undefined' || !rawUrl) {
      return rawUrl;
    }

    // Prevent mixed-content errors when UI is served over HTTPS.
    if (window.location.protocol === 'https:') {
      try {
        const parsed = new URL(rawUrl, window.location.origin);
        if (parsed.protocol === 'ws:') {
          // If the socket target is this same host, use same-origin HTTPS/WSS so
          // reverse proxies (nginx/traefik) can terminate TLS and forward internally.
          if (parsed.hostname === window.location.hostname) {
            const normalizedPath = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
            return `${window.location.origin}${normalizedPath}`;
          }

          parsed.protocol = 'wss:';
          return parsed.toString();
        }
      } catch {
        if (rawUrl.startsWith('ws://')) {
          const target = rawUrl.slice('ws://'.length);
          if (target.startsWith(`${window.location.hostname}:`)) {
            return window.location.origin;
          }
          return `wss://${target}`;
        }
      }
    }

    return rawUrl;
  }

  function connect(token?: string) {
    // The desktop launcher home holds no hub connection at all (card
    // t_f0ea3681): `/home` in Electron is the picker, every server opens in its
    // own tab, and a socket opened here is one the operator never asked for —
    // it is what delivered the last hub's notices to the home page (balloon,
    // tone, speech). The guard is here rather than at each caller so that *no*
    // path can open one: the boot path (`auth.ts` checkAuth after a restored
    // session), the visibility handler, a connect dialog, a future caller. The
    // stored session, the cloud list and the LAN probe are not hub sockets and
    // are untouched by this.
    if (onDesktopLauncherHome()) {
      console.info('[SocketStore] Launcher home — the desktop picker opens no hub socket');
      return;
    }

    const config = getRuntimeConfig();

    if (config.mode === 'cloud' || webrtcStore.connected) {
      if (isStatusBridge(socket.value) && connected.value) {
        debugSocket('[SocketStore] WebRTC status bridge already connected, skipping');
        return;
      }

      // Switching to the WebRTC transport: drop a leftover local socket from a
      // previous hub session. Otherwise it keeps running in the background and
      // `socket.value` would hold the dead local socket instead of the bridge.
      if (socket.value && !isStatusBridge(socket.value)) {
        disconnect();
      }

      socket.value = markRaw(statusBridgeSocket);
      socketTargetUrl = null;
      connected.value = webRTCStatusBridge.connected && webrtcStore.connected;
      debugSocket('[SocketStore] Using WebRTC status bridge for active WebRTC session', {
        mode: config.mode,
        connected: connected.value,
      });
      return;
    }

    // In browser local mode use '/' so requests pass through the Vite proxy or
    // nginx. Electron desktop windows are often loaded from file://, so they
    // must connect to the hub using an absolute wsUrl instead of a relative path.
    // Capacitor also needs the full wsUrl because '/' resolves to the device.
    const socketUrl =
      config.mode === 'local' && !isCapacitor()
        ? '/' // Browser local mode: Vite proxy / nginx handles routing
        : resolveSocketUrl(config.wsUrl); // Electron / Capacitor / cloud: direct hub connection

    // Reuse an existing socket only when it targets the SAME URL. A socket left
    // over from a previous hub — or the (possibly torn-down) WebRTC status
    // bridge — must be replaced, otherwise this connect() is a silent no-op and
    // the new hub never receives status events (commands still work over HTTP:
    // the classic "lamp toggles but no status" symptom). Callers no longer need
    // to disconnect() first (e.g. enablePanelMode racing the router guard still
    // reuses the in-flight socket for the same target).
    if (socket.value) {
      if (sameSocketTarget(socketTargetUrl, socketUrl)) {
        debugSocket('[SocketStore] Socket already exists for the same target, skipping');
        return;
      }
      debugSocket('[SocketStore] Replacing socket (different target or transport)', {
        existingUrl: socketTargetUrl ?? '(bridge/unknown)',
        socketUrl,
      });
      disconnect();
    }

    debugSocket('[SocketStore] Connecting', {
      mode: config.mode,
      socketUrl,
      configuredWsUrl: config.wsUrl,
      hasToken: !!token,
    });

    socket.value = io(socketUrl, {
      auth: {
        token: token || '',
        deviceId: getOrCreateDeviceFingerprint(),
      },
      // Allow polling fallback — on Android WebView the initial WebSocket
      // upgrade can fail transiently; polling lets the connection succeed and
      // then upgrade to WS on the next reconnect.
      transports: ['websocket', 'polling'],
    });
    socketTargetUrl = socketUrl;

    socket.value.on('connect', () => {
      connected.value = true;
      debugSocket('[SocketStore] Socket connected successfully');

      // Report media device capabilities to the server
      if (navigator.mediaDevices?.enumerateDevices) {
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            const hasCamera = devices.some((d) => d.kind === 'videoinput');
            const hasMicrophone = devices.some((d) => d.kind === 'audioinput');
            socket.value?.emit('userDevice:capabilities', { hasCamera, hasMicrophone });
          })
          .catch(() => undefined);
      }

      // Check if we need to refresh entities after reconnection
      if (lastDisconnectTime.value > 0) {
        const disconnectDuration = Date.now() - lastDisconnectTime.value;
        debugSocket(`[SocketStore] Reconnected after ${Math.round(disconnectDuration / 1000)}s`);

        const entitiesStore = useEntitiesStore();
        if (entitiesStore.isStale()) {
          debugSocket('[SocketStore] Data is stale, refreshing entities...');
          entitiesStore.refresh();
        }
      }

      // Re-anchor the demo clock frame (card D1). `demo:clock` only flows while
      // the demo PLAYS, so a reconnect into a paused demo would otherwise leave
      // every clock on real time until a clock surface mounted. One ping — the
      // NTP offset stays `syncClock`'s job, and this never re-runs it.
      if (socket.value) void refreshDemoFrame(socket.value);
    });

    socket.value.on('disconnect', () => {
      connected.value = false;
      lastDisconnectTime.value = Date.now();
      debugSocket('[SocketStore] Socket disconnected');
    });

    socket.value.on('error', (error: unknown) => {
      console.error('[SocketStore] ✗ Socket error:', error);
    });

    socket.value.on('connect_error', (error: unknown) => {
      console.error('[SocketStore] ✗ Connection error:', error);
      const msg = (error instanceof Error ? error.message : '').toLowerCase();
      if (msg.includes('auth') || msg.includes('token') || msg.includes('unauthorized') || msg.includes('jwt')) {
        console.warn('[SocketStore] Auth failure detected, redirecting to connect screen');
        const authStore = useAuthStore();
        void authStore.notifySessionExpired();
      }
    });

    // The demo clock's own events — ONE listener set for the whole app (card D1,
    // plan §3.5). `demo:clock` is the 1 Hz frame while the demo plays and
    // `demo:state` is every mode/rate/seek change; both land in
    // `services/clockSync`, which every clock surface already reads through
    // `serverNow()` (the dashboard clock widgets, the BIM sun).
    detachDemoClock = attachDemoClockListeners(socket.value);
    // …and the demo's READ feeds (card D8b): `demo:weather` (the animated sky),
    // `demo:people` (the family in the 3D model) and `demo:event` (the run log).
    // Same rule as the clock: one listener set for the app, attached with the
    // socket it belongs to and detached with it.
    detachDemoFeed = attachDemoFeedListeners(socket.value);
    // …and the demo's narrated captions (card D16): `demo:narration` is one
    // event per cue change, carrying the line, its live data row and one
    // rendered clip per language. Same rule again — one listener set for the
    // app, attached with the socket and detached with it (a cue is a caption
    // and a voice, so a stale subscription would speak twice).
    detachDemoNarration = attachDemoNarrationListeners(socket.value);
  }

  function disconnect() {
    // The demo clock goes with the hub it belonged to: the next hub must not
    // inherit this one's sim time or offset (its own connect re-anchors the
    // frame through `refreshDemoFrame`).
    detachDemoClock?.();
    detachDemoClock = null;
    detachDemoFeed?.();
    detachDemoFeed = null;
    detachDemoNarration?.();
    detachDemoNarration = null;
    resetDemoFeed();
    resetClockSync();

    if (isStatusBridge(socket.value)) {
      socket.value = null;
      socketTargetUrl = null;
      connected.value = false;
      return;
    }

    if (socket.value) {
      try {
        // Only real socket.io clients implement disconnect(); guard anyway so
        // an unexpected object can never wedge logout/hub switching again.
        if (typeof socket.value.disconnect === 'function') {
          socket.value.disconnect();
        }
      } catch (error) {
        console.warn('[SocketStore] Socket disconnect failed (safe to ignore):', error);
      }
      socket.value = null;
      socketTargetUrl = null;
      connected.value = false;
    }
  }

  function subscribe(type: string, id: string) {
    // console.log('[SocketStore] Subscribing to room:', `${type}:${id}`, 'connected:', connected.value);
    socket.value?.emit('subscribe', { type, id });
  }

  function unsubscribe(type: string, id: string) {
    // console.log('[SocketStore] Unsubscribing from room:', `${type}:${id}`);

    socket.value?.emit('unsubscribe', { type, id });
  }

  function on(event: string, callback: SocketHandler) {
    // console.log('[SocketStore] Registering listener for event:', event);
    socket.value?.on(event, callback);
  }

  function off(event: string, callback?: SocketHandler) {
    // console.log('[SocketStore] Removing listener for event:', event, callback ? '(specific handler)' : '(all handlers)');
    socket.value?.off(event, callback);
  }

  return {
    socket,
    connected,
    lastDisconnectTime,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    on,
    off,
  };
});
